import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {Select} from 'primeng/select';
import {DatePipe} from '@angular/common';
import {PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {ActivatedRoute, Router} from '@angular/router';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {MessageService} from 'primeng/api';
import {distinctUntilChanged, map, switchMap, tap} from 'rxjs/operators';
import {timer} from 'rxjs';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {UserOverviewDto, UsersClient} from '../../api-client';
import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {UserLinkComponent} from '../../shared/components/user-link/user-link.component';
import {homeyStatusToTag, paymentStatusToTag} from '../../shared/enum-to-tag';
import {DATE_TIME_FORMAT} from '../../shared/formats';
import {shallowEqual, updateQueryParams} from '../../shared/query-params';
import {userDisplayName} from '../../shared/user-display-name';
import {describeHttpError, silentErrors} from '../../shared/http-context';

type View = 'all' | 'to-sync' | 'active' | 'mandatory-flow';

const viewOptions = [
  {label: 'All', value: 'all'},
  {label: 'To be synced soon', value: 'to-sync'},
  {label: 'Active', value: 'active'},
  {label: 'Mandatory Flow', value: 'mandatory-flow'},
] as const;

const isView = (value: unknown): value is View => viewOptions.some((option) => option.value === value);

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'homey-users-page',
  standalone: true,
  imports: [
    FormsModule,
    Select,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    UserLinkComponent,
    InputText,
    Button,
  ],
  templateUrl: './users-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersPageComponent {
  private readonly usersClient = inject(UsersClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly users = signal<UserOverviewDto[]>([]);
  protected readonly totalCount = signal(0);
  // The index reports a window rather than a total when a query is too broad to enumerate.
  protected readonly truncated = signal(false);
  protected readonly loading = signal(false);

  protected readonly idLookup = signal('');

  protected readonly emailSearch = signal('');
  protected readonly emailSearchLoading = signal(false);

  protected readonly phoneSearch = signal('');
  protected readonly phoneSearchLoading = signal(false);

  // The router writes raw URL strings (or undefined when the query param is absent), so these
  // inputs cannot be typed as literal unions - they are narrowed below instead.
  public readonly view = input<string | undefined>(undefined);
  protected readonly finalView = computed<View>(() => {
    const value = this.view();
    return isView(value) ? value : 'all';
  });

  public readonly search = input<string | undefined>('');
  protected readonly viewOptions = [...viewOptions];

  protected readonly dateTimeFormat = DATE_TIME_FORMAT;
  protected readonly userDisplayName = userDisplayName;

  protected readonly searchParams = computed(() => ({
    search: this.search() ?? '',
    view: this.finalView(),
  }));

  constructor() {
    toObservable(this.searchParams)
      .pipe(
        // switchMap+timer rather than debounceTime: index 0 is the initial load and goes straight
        // through, so arriving on this page does not cost 300ms of dead time before the request
        // even starts. Later emissions (typing) still debounce, because switchMap cancels the
        // pending timer when a new one arrives.
        switchMap((params, index) => timer(index === 0 ? 0 : 300).pipe(map(() => params))),
        distinctUntilChanged(shallowEqual),
        tap(() => this.loading.set(true)),
        switchMap(({search, view}) =>
          this.usersClient.searchUsers(search, view === 'all' ? undefined : view)
        ),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (result) => {
          this.users.set(result.users);
          this.totalCount.set(result.totalCount);
          this.truncated.set(result.truncated);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  protected onModeChange(view: View): void {
    updateQueryParams(this.router, this.route, {view});
  }

  protected onSearchChange(value: string): void {
    updateQueryParams(this.router, this.route, {search: value});
  }

  protected openById(): void {
    const id = this.idLookup().trim();
    if (!id) {
      return;
    }

    if (!uuidPattern.test(id)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid user ID',
        detail: 'A user ID looks like 123e4567-e89b-12d3-a456-426614174000.',
      });
      return;
    }

    this.router.navigate(['/users', id]);
  }

  protected searchByEmail(): void {
    if (this.emailSearchLoading()) return;

    const email = this.emailSearch().trim();
    if (!email) return;

    this.emailSearchLoading.set(true);
    this.usersClient
      .findUserByEmail(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => {
          this.emailSearchLoading.set(false);

          if (users.length === 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'No user found',
              detail: `No user with email ${email}`,
            });
            return;
          }

          const match = users[0];
          if (users.length === 1 && match) {
            this.router.navigate(['/users', match.id]);
            return;
          }

          this.users.set(users);
          this.totalCount.set(users.length);
          this.truncated.set(false);
          this.messageService.add({
            severity: 'info',
            summary: 'Multiple users found',
            detail: `${users.length} users share email ${email}. Pick the right one from the table.`,
          });
        },
        error: () => {
          this.emailSearchLoading.set(false);
        },
      });
  }

  protected searchByPhone(): void {
    if (this.phoneSearchLoading()) return;

    const phone = this.phoneSearch().trim();
    if (!phone) return;

    this.phoneSearchLoading.set(true);
    this.usersClient
      // silentErrors: a 404 here means "nobody has that number", which is an answer, not a
      // failure. Without it the operator gets a red "Request failed - 404" next to the warning
      // below.
      .findUserByPhone(phone, 'body', false, {context: silentErrors()})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({userId}) => {
          this.phoneSearchLoading.set(false);
          this.router.navigate(['/users', userId]);
        },
        error: (err: unknown) => {
          this.phoneSearchLoading.set(false);
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.messageService.add({
              severity: 'warn',
              summary: 'No user found',
              detail: `No user with phone number ${phone}`,
            });
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Phone lookup failed',
            // Same formatting as the interceptor's toast, so opting out does not mean a worse message.
            detail: err instanceof HttpErrorResponse ? describeHttpError(err) : 'Unexpected error',
          });
        },
      });
  }

  protected readonly homeyStatusToTag = homeyStatusToTag;
  protected readonly paymentStatusToTag = paymentStatusToTag;
}
